import React, { useState, useEffect } from 'react';
import Header from '../Layout/Header';
import { useFirebase } from '../../hooks/useFirebase';
import './ParticipantView.css';

const ParticipantView: React.FC = () => {
  const { connectionStatus, participantCount, responses, currentQuestion, joinAsParticipant, updateResponse } = useFirebase();
  const [sliderValue, setSliderValue] = useState<number>(50);
  const [hasJoined, setHasJoined] = useState<boolean>(false);
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const [showResults, setShowResults] = useState<boolean>(false);

  useEffect(() => {
    if (connectionStatus === 'connected' && !hasJoined) {
      joinAsParticipant();
      setHasJoined(true);
    }
  }, [connectionStatus, hasJoined, joinAsParticipant]);

  // Show results immediately after submitting
  useEffect(() => {
    if (hasSubmitted) {
      setShowResults(true);
    }
  }, [hasSubmitted]);

  // Handle session reset by watching for empty responses
  useEffect(() => {
    const responseCount = Object.keys(responses).length;
    
    // If responses are cleared (session reset) and we had submitted before
    if (responseCount === 0 && hasSubmitted) {
      console.log('🔄 Session reset detected - returning to slider');
      setHasSubmitted(false);
      setShowResults(false);
      setSliderValue(50); // Reset to default
      
      // Re-join as participant after reset
      console.log('🙋 Re-joining as participant after reset');
      setHasJoined(false); // Trigger rejoin in the first useEffect
    }
  }, [responses, hasSubmitted]);

  const handleSliderChange = (value: number) => {
    setSliderValue(value);
  };

  const handleSendAnswer = () => {
    if (connectionStatus === 'connected' && !hasSubmitted) {
      updateResponse(sliderValue);
      setHasSubmitted(true);
    }
  };

  const calculateAverage = () => {
    const values = Object.values(responses).map((r: any) => r.value);
    if (values.length === 0) return 0;
    return Math.round(values.reduce((sum, val) => sum + val, 0) / values.length);
  };

  const responseCount = Object.keys(responses).length;
  const average = calculateAverage();

  if (showResults) {
    return (
      <div className="participant-view">
        <Header
          showLogo={true}
          participantCount={participantCount}
          connectionStatus={connectionStatus}
        />
        
        <main className="participant-main">
          <div className="container">
            <div className="results-section">
              <h2 className="results-title">Question Results</h2>
              
              <div className="your-response">
                <span className="response-label">Your response:</span>
                <span className="response-value">{sliderValue}</span>
              </div>

              {responseCount > 0 && (
                <div className="results-visualization">
                  <div className="response-dots">
                    {Object.entries(responses).map(([socketId, response]: [string, any]) => (
                      <div
                        key={socketId}
                        className="response-dot"
                        style={{
                          left: `${response.value}%`,
                          backgroundColor: '#9CB0BD' // Dory Lighter Blue
                        }}
                        title={`Response: ${response.value}`}
                      />
                    ))}
                    {/* Average indicator - only show if more than one response */}
                    {responseCount > 1 && (
                      <div
                        className="average-dot"
                        style={{
                          left: `${average}%`,
                          backgroundColor: '#435F60' // Dory Green
                        }}
                        title={`Average: ${average}`}
                      />
                    )}
                  </div>
                  
                  <div className="results-scale">
                    <span className="scale-label">0</span>
                    {responseCount > 1 && (
                      <span className="scale-label average-label">Average: {average}</span>
                    )}
                    <span className="scale-label">100</span>
                  </div>
                </div>
              )}

              {responseCount === 0 && (
                <div className="no-responses-yet">
                  <p>No responses received yet</p>
                  <p className="help-text">Results will appear as responses come in</p>
                </div>
              )}

              <div className="waiting-message">
                <p className="help-text">Results will update as more responses come in</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="participant-view">
      <Header
        showLogo={true}
        participantCount={participantCount}
        connectionStatus={connectionStatus}
      />
      
      <main className="participant-main">
        <div className="container">
          <div className="response-section">
            <h2 className="response-title">
              {currentQuestion || "Waiting for first question..."}
            </h2>
            
            <div className="slider-container">
              <div className="slider-track">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={sliderValue}
                  onChange={(e) => handleSliderChange(parseInt(e.target.value))}
                  className="response-slider"
                  disabled={hasSubmitted}
                />
                <div 
                  className="slider-value"
                  style={{ left: `${sliderValue}%` }}
                >
                  {sliderValue}
                </div>
              </div>
              
              <div className="slider-labels">
                <span className="slider-label">0</span>
                <span className="slider-label">100</span>
              </div>
            </div>

            {!hasSubmitted && (
              <div className="send-answer-section">
                <button 
                  onClick={handleSendAnswer}
                  className="btn-primary send-answer-button"
                  disabled={connectionStatus !== 'connected'}
                >
                  Send Answer
                </button>
              </div>
            )}
            
            {hasSubmitted && (
              <div className="submitted-message">
                <p>✅ Response submitted!</p>
                <p>Waiting for results...</p>
              </div>
            )}
            
            {/* Orientation hint for mobile */}
            {!hasSubmitted && (
              <div className="mobile-hint">
                💡 Rotate phone horizontally for easier control
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ParticipantView; 